import { createElement, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import { Element } from 'common/interfaces'
import { DataSource } from 'components/DataSource'
import StyledWrap from 'components/StyledWrap'
import { MustacheCustomRender, useCurrentPage, useTemplateCustomRender } from 'utils/hook'
import { renderRule } from 'views/Flow/renderRule'

import components from '../componentsMap'

export function renderElement(node: Element, render?: MustacheCustomRender): JSX.Element | null {
  const Component = components[node.type]

  const isComponentRender =
    node.render_rules.length > 0 && render !== undefined
      ? renderRule(node.render_rules, render)
      : true

  if (!isComponentRender) {
    return null
  }

  if (node.events.length !== 0) {
    return <DataSource node={node} Component={Component} />
  }

  return (
    <StyledWrap elementName={node.type} key={`styled-wrap-${node.name}`}>
      {
        // eslint-disable-next-line react/no-children-prop
        createElement(Component, {
          ...node.props,
          name: node.name,
          key: node.name,
          children: node.children,
          validations: node.validations,
        })
      }
    </StyledWrap>
  )
}

export default function FormWizard(): JSX.Element {
  const page = useCurrentPage()

  const render = useTemplateCustomRender()
  const { setValue } = useFormContext()

  /*
   * This useEffect clears any radio groups with onload events on page navigation.
   * This prevents stale data from being pre-selected on potential dynamic data changes.
   * Original reason for implementing: Slide claim contacts were recreated each time a policy search happened.
   * Relevant thread: https://liberateinc.slack.com/archives/C09365NU6G5/p1751318697019039?thread_ts=1750970530.237869&cid=C09365NU6G5
   * In the future, if we need to clear onload data for other element types, we can add them here.
   * Alternatively, if we don't want to clear the data, a prop can be added to the element to prevent it from being cleared
   * or this useEffect can be removed.
   */
  useEffect(() => {
    const clearOnloadFields = (): void => {
      page.elements.forEach((element: Element) => {
        const isElementRender =
          element.render_rules.length > 0 && render !== undefined
            ? renderRule(element.render_rules, render)
            : true

        if (
          element.events?.some((event) => event.type === 'onload') &&
          isElementRender &&
          element.type === 'RadioGroup'
        ) {
          setValue(element.name, '')
        }
      })
    }

    clearOnloadFields()
  }, [page.id, setValue])

  return <>{page.elements.map((el) => renderElement(el, render))}</>
}
